import React from "react";
import { ActionList, debounce, IIconTextLinkProps } from "component-library";

import { ChatBoxIcon, NotesIcon, PrintIcon } from "assets";

import {
  IAgencyList,
  ICastingNotes,
  INewShortlist,
  IShortlistPerformer,
  IShortlists,
} from "models";
import { CastingDirectorNotes } from "./components";
import { ShortlistSection } from "./components/ShortlistSection/ShortlistSection";

import "./ActionsFooterRow.scss";

export interface IActionsFooterRowContent {
  shortlistLabel: string;
  shortlistPlaceholder: string;
  shortlistSearchPlaceholder: string;
  newShortlistLabel: string;
  newShortlistOption: string;
  shortlistRequired: string;
  shortlistNameRequired: string;
  addToShortlistLabel: string;
  printLabel: string;
  emailLabel: string;
  viewNotesLabel: string;
  addToShortlistHeader: string;
  saveShortlist: string;
  cancelShortlist: string;
  moreButtonLabel: string;
  performerAddedToast: string;
}

export interface IActionsFooterRowProps {
  content: IActionsFooterRowContent;
  shortlists?: IShortlists;
  agencies?: IAgencyList;
  notes?: ICastingNotes;
  saveNotes: (notes: ICastingNotes) => void;
  printPage: () => void;
  sendEmail: () => void;
  initShortlistLoading: () => void;
  addShortList: (model: INewShortlist) => Promise<string>;
  addPerformerShortList: (
    shortlistId: number,
    model: IShortlistPerformer
  ) => Promise<string>;
}

interface IActionsFooterRowState {
  isNotesVisible: boolean;
  notes?: string;
}

export class ActionsFooterRow extends React.Component<
  IActionsFooterRowProps,
  IActionsFooterRowState
> {
  public static getDerivedStateFromProps(
    { notes: notesModel }: IActionsFooterRowProps,
    { notes }: IActionsFooterRowState
  ) {
    const newState: Partial<IActionsFooterRowState> = {};

    if (notesModel && notes === undefined) {
      newState.notes = notesModel.content;
      newState.isNotesVisible = notesModel.content.length > 0;
    }

    return newState;
  }

  public readonly state: Readonly<IActionsFooterRowState> = {
    isNotesVisible: false,
  };

  private notesActions: IIconTextLinkProps[];

  constructor(props: IActionsFooterRowProps) {
    super(props);

    this.initActions();
  }

  public render() {
    const {
      content,
      agencies,
      shortlists,
      addPerformerShortList,
      addShortList,
    } = this.props;

    return (
      <div className="c-actions-footer-row g-col-lg-12 g-col-md-12 g-inner-grid">
        <div className="g-col-lg-4 g-col-md-5 g-col-sm-12">
          <div className="c-actions-footer-row__notes-anchor">
            {this.renderNotes()}
          </div>
          <div className="c-actions-footer-row__button">
            {this.renderButtons()}
          </div>
        </div>
        <div className="c-actions-footer-row__notes-buffer g-col-lg-1" />
        <ShortlistSection
          addPerformerShortList={addPerformerShortList}
          addShortList={addShortList}
          content={content}
          agencies={agencies}
          shortlists={shortlists}
        />
      </div>
    );
  }

  public componentDidMount() {
    this.props.initShortlistLoading();
  }

  private renderButtons = () => {
    const { content } = this.props;

    return (
      <ActionList
        variant="pageHeader"
        items={this.notesActions}
        moreButtonText={content.moreButtonLabel}
        moreElementsDirection="up"
      />
    );
  };

  private renderNotes = () => {
    const { notes, isNotesVisible } = this.state;
    const notesContainerClasses = `c-actions-footer-row__notes-container ${
      isNotesVisible ? "active" : ""
    }`;
    const notesClasses = `c-actions-footer-row__notes ${
      isNotesVisible ? "active" : ""
    }`;

    return (
      <div className={notesContainerClasses}>
        <div className={notesClasses}>
          <CastingDirectorNotes
            notes={notes}
            changeNotes={this.changeNotes}
            closeNotes={this.closeNotes}
          />
        </div>
      </div>
    );
  };

  private initActions = () => {
    const { content, printPage, sendEmail } = this.props;

    this.notesActions = [
      {
        text: content.viewNotesLabel,
        svgIconStart: <NotesIcon />,
        variant: "secondary",
        handleClick: this.toggleNotes,
        id: "notesButton",
      },

      {
        text: content.printLabel,
        svgIconStart: <PrintIcon />,
        variant: "secondary",
        handleClick: printPage,
        id: "printButton",
      },
      {
        text: content.emailLabel,
        svgIconStart: <ChatBoxIcon />,
        variant: "secondary",
        handleClick: sendEmail,
        id: "emailButton",
      },
    ];
  };

  private toggleNotes = () =>
    this.state.isNotesVisible ? this.closeNotes() : this.openNotes();

  private openNotes = () => this.setState({ isNotesVisible: true });

  private changeNotes = (notes: string) => {
    this.setState({ notes });
    this.saveNotesDebounced();
  };

  private closeNotes = () => {
    this.setState({ isNotesVisible: false });
    this.saveNotes();
  };

  private saveNotes = () => {
    const { notes: notesText = "" } = this.state;
    const { saveNotes, notes: { content: prevContent = "" } = {} } = this.props;

    if (prevContent !== notesText) {
      saveNotes({ content: notesText });
    }
  };

  private saveNotesDebounced = debounce(this.saveNotes, 1000);
}
